/**
 * useModalChrome — the ONE modal chrome in the ecosystem (MODALCHROME-001, owner ruling R-9,
 * 2026-09-18).
 *
 * Everything that must happen because a dialog opened, and be undone when it closes: the body
 * scroll lock, Escape-to-close, focus capture / initial focus / guarded restore, the Tab cycle that
 * keeps focus inside, the backdrop dismissal (`backdropProps`), and the `useId` for `aria-labelledby`.
 *
 * ## Why this exists
 *
 * THREE implementations of this one concern coexisted, and **none was exported**:
 *
 * | concern | page-shell `useModalLifecycle` | login-shell `useModalPresentation` + `useFocusTrap` | pos-web `useModalA11y` |
 * | --- | --- | --- | --- |
 * | scroll lock | `body.modal-open` class | `body.style.overflow` inline | both |
 * | Escape | document-level, opt-out | — | a handler the caller must bind |
 * | focus restore | guarded by `document.contains` | unguarded | unguarded |
 * | initial focus | `initialFocusRef`, SYNCHRONOUS | first focusable, `setTimeout(0)` | first focusable, `setTimeout(0)` |
 * | **Tab cycle** | **NO** | yes | yes |
 * | backdrop / titleId | in `<Modal>` | in the hook | handler only |
 *
 * pos-web's own docblock called the convergence a follow-up that *"needs its own behavioural
 * diffing, not a silent swap."* That diffing is the table above; every divergence below is resolved
 * deliberately, and the reason is recorded next to it.
 *
 * 🔴 **The row that mattered: page-shell had no Tab cycle.** Every `<Modal>` in the fleet — and the
 * emergency-broadcast overlay, which claims `role="alertdialog"` and blocks the whole app — could be
 * tabbed straight out of. axe cannot detect a missing focus trap, which is why they all passed a11y
 * review.
 *
 * ## Why it is a LEAF PACKAGE and not part of page-shell
 *
 * It started in `@puffin-core/page-shell`, and three packages that need it could not reach it
 * there: page-shell DEPENDS ON `command-palette`, `keyboard-shortcuts` and `location-picker`
 * (`<AppShell>` composes all three), so an import back into page-shell writes a dependency CYCLE.
 * All three hand-rolled their own focus trap, which is the exact defect this hook exists to end.
 * Its entire import cone is `react`, so anything may depend on it.
 *
 * @module @puffin-core/dialog-chrome/useModalChrome
 */

import { useCallback, useEffect, useId, useState } from 'react';
import { useDialogStackEntry } from './useDialogStackEntry.js';
import { useBackdropDismissal } from './useBackdropDismissal.js';

/**
 * What counts as focusable for the Tab cycle. Re-queried on every Tab rather than captured once at
 * bind, so a dialog whose content changes (a wizard step, a revealed error) still cycles correctly —
 * the pos-web copy captured first/last at bind time and went stale.
 *
 * 🔴 `:not([disabled])` is LOAD-BEARING, and its absence was a live trap (found in a browser walk
 * of planner-web's `<PlanTemplateDrawer>`, MODALCHROME-BURNDOWN-001 L0B, 2026-09-20).
 *
 * A **disabled Save button is the last focusable in a great many dialogs** — the standard pattern
 * is to disable it until the form validates, so it is disabled exactly when the dialog opens. With
 * it counted as `last`, Shift-Tab from the first element did this:
 *
 *   1. the handler matched `document.activeElement === first`;
 *   2. it called `event.preventDefault()`, KILLING the browser's own backward move;
 *   3. it called `last.focus()` — **a silent no-op on a disabled element**.
 *
 * Focus stayed on the close button and the user could not Shift-Tab out of it. That is strictly
 * worse than having no trap at all: a missing trap lets focus escape, this CANCELS the native
 * behaviour and substitutes nothing. Measured live: `shiftTabPrevented: true`,
 * `landedOnLast: false`, `focusStuckOnFirst: true`.
 *
 * `[href]` is narrowed to `a[href], area[href]` in the same pass: the bare attribute selector also
 * matched `<link>` and any element someone had put an `href` on, neither of which is focusable.
 *
 * 🔴 `:not([aria-hidden="true"])` is the SECOND convergence, added 2026-09-20 when
 * `<PaletteShell>` was migrated. Two of the fleet's hand-rolled traps filtered hidden nodes and
 * this hook did not, so adopting it unchanged would have REMOVED that filtering — a consolidation
 * has to carry the best behaviour of everything it absorbs, not the average. An element that is
 * `aria-hidden` but focusable is invisible to a screen reader and still a Tab stop, which puts a
 * keyboard user somewhere their AT cannot describe.
 *
 * Both copies spelled it `!el.hasAttribute('aria-hidden')`, which also filters
 * `aria-hidden="false"` — an explicit declaration that the element is NOT hidden. Matching on the
 * VALUE is what those two meant; it is not a behaviour change they will notice, and it is the only
 * spelling that is correct.
 *
 * ⚠️ Known limit, stated rather than silently accepted: this tests the element's OWN attribute, so
 * a focusable inside an `aria-hidden="true"` SUBTREE still cycles. Both copies had the same limit.
 * Widening it needs an ancestor walk per candidate per keystroke; queued as a question rather than
 * taken here, because no consumer has asked for it and the cost is per-Tab.
 */
const NOT_ARIA_HIDDEN = ':not([aria-hidden="true"])';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
]
  .map((base) => `${base}${NOT_ARIA_HIDDEN}`)
  .join(', ');

/** Pairs with `body.modal-open { overflow: hidden }` in shared-styles' `modals.css`. */
const BODY_OPEN_CLASS = 'modal-open';

/**
 * @param {Object} params
 * @param {boolean} params.isOpen
 * @param {Function} params.onClose - Called by Escape and by a backdrop dismissal (a press that
 *   went down AND came up on the backdrop itself — `useBackdropDismissal`).
 * @param {Function} [params.onOpen] - Stable (`useCallback`) hook fired as the dialog opens, BEFORE
 *   the scroll lock and focus capture. The seam a dialog uses to reset its own state.
 * @param {boolean} [params.closeOnEscape=true] - `false` for a dialog that must not be dismissed —
 *   focus is still trapped and still restored, only the exit is removed.
 * @param {boolean} [params.closeOnBackdrop=true] - As above, for the backdrop.
 * @param {{current: HTMLElement|null}} [params.initialFocusRef] - Focused synchronously on open.
 * @returns {{containerRef: (node: HTMLElement|null) => void, titleId: string,
 *   backdropProps: import('./useBackdropDismissal.js').BackdropProps}} `backdropProps` goes on the backdrop element
 *   whole (`<DialogScrim backdropProps={backdropProps}>`, or spread on a hand-placed backdrop); never only its
 *   `onClick`, which alone cannot tell a swipe out of the dialog from a click on the backdrop.
 *   `containerRef` is a stable CALLBACK ref — put it
 *   on the dialog element exactly as you would a ref object (`ref={containerRef}`). It is a
 *   callback and not a ref object on purpose; see "Why the container is state" below.
 */
export function useModalChrome({
  isOpen,
  onClose,
  onOpen,
  closeOnEscape = true,
  closeOnBackdrop = true,
  initialFocusRef,
}) {
  // ── Why the container is STATE and the ref is a CALLBACK ──────────────────────────────────
  // 🔴 A `useRef` container cannot tell an effect that the node arrived. Writing `ref.current`
  // does not re-render and does not re-run anything, so an effect that early-returns on
  // `!ref.current` NEVER RUNS AGAIN — and the Tab cycle is simply absent for the rest of the
  // dialog's life. Measured (MODALCHROME-BURNDOWN-001 L1, 2026-09-20): a dialog whose container
  // mounts one commit after `isOpen` flips true let Tab walk straight out to the page behind it.
  //
  // A caller-supplied dep array was the previous answer to this (`focusTrapDeps`) and it is the
  // wrong instrument twice over: it asks every caller to GUESS which of its own state variables
  // gate the container's existence, and it silently does nothing when they guess wrong. It was
  // also the only reason this hook needed a `react-hooks/exhaustive-deps` suppression — a spread
  // element in a dep array cannot be statically verified. It is PRUNED, not passed through.
  //
  // A state-backed callback ref removes the failure mode by construction: React invokes it on
  // attach and on detach, so the binding effect runs exactly when there is (or stops being) a
  // node, whenever that happens. The callback's identity is stable, so React does not detach and
  // re-attach on every render.
  const [container, setContainer] = useState(null);
  const containerRef = useCallback((node) => {
    setContainer(node);
  }, []);
  const titleId = useId();

  // ── Where focus came FROM, captured during RENDER ─────────────────────────────────────────
  // 🔴 It has to be render, not an effect, and the reason is `autoFocus`. React applies a child's
  // `autoFocus` during the COMMIT — before any effect in this hook runs — so by the time an
  // effect reads `document.activeElement` on a dialog containing an autofocusing field, the
  // trigger is already gone and what it captures is the dialog's own input. That element is
  // detached on close, the `document.contains` guard correctly refuses to focus a detached node,
  // and **focus silently falls to `<body>`**: the user is dumped at the top of the page with no
  // way back to what they were doing. Measured on `<SkillEditModal>` (its name field carries
  // `autoFocus`) — `activeElement` after close was `<body>`, not the trigger. Every dialog in the
  // fleet with an autofocusing field had this, before and after the three-copy convergence.
  //
  // `wasOpen` starts FALSE rather than at `isOpen` on purpose, so a dialog that MOUNTS already
  // open still counts as a transition and still captures. This is React's documented
  // "adjust state during render from the previous value" pattern: the `set` calls below are made
  // during render, React discards this pass and re-renders immediately without committing, and
  // the guard makes the second pass a no-op. Reading `document.activeElement` here is the one
  // impure act, and it is the only moment at which the answer is correct.
  const [wasOpen, setWasOpen] = useState(false);
  const [openedFrom, setOpenedFrom] = useState(null);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    setOpenedFrom(isOpen && typeof document !== 'undefined' ? document.activeElement : null);
  }

  // ── Body scroll lock ──────────────────────────────────────────────────────────────────────
  // The CLASS, never `body.style.overflow`. Inline style is forbidden ecosystem-wide, and the
  // class is what `modals.css` already pairs with; two of the three copies set the inline style
  // and one set both.
  useEffect(() => {
    if (!isOpen) return undefined;
    if (onOpen) onOpen();
    document.body.classList.add(BODY_OPEN_CLASS);
    return () => {
      document.body.classList.remove(BODY_OPEN_CLASS);
    };
  }, [isOpen, onOpen]);

  // ── Escape + escaped-focus recovery, via the STACK ────────────────────────────────────────
  // Escape is still handled at DOCUMENT level — pos-web's container-bound copy did nothing until
  // the user had tabbed in — but it is no longer handled by a listener PER DIALOG. Each dialog
  // registers itself while open and `dialogStack` runs one shared capture-phase listener that asks
  // who is on top. See that module for why per-dialog listeners cannot be made correct: with them,
  // `stopImmediatePropagation` hands the key to whichever dialog opened FIRST — the outer one.
  //
  // The stack also owns the recovery of focus that has fallen to `<body>` (a focused element that
  // unmounted), which the container-bound Tab cycle cannot reach by construction.
  //
  // The registration itself lives in `useDialogStackEntry` — not for tidiness, but because adding
  // it inline took this hook to 13 hooks / 5 effects, past `web/excessive-hooks-usage`. That rule
  // is structural and may not be suppressed, so the only move is to decompose.
  useDialogStackEntry({ isOpen, onClose, closeOnEscape, container });

  // ── Guarded restore ───────────────────────────────────────────────────────────────────────
  // Putting focus back is a fact about the open/close transition, not about the container, so the
  // container is deliberately NOT in these deps: the binding effect below must be free to re-run
  // when a node attaches without this one running its restore mid-dialog.
  //
  // GUARDED by `document.contains`: two of the three copies this hook replaced restored focus to
  // a possibly-detached node, which steals focus from wherever the app has since moved it.
  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      if (
        openedFrom &&
        typeof openedFrom.focus === 'function' &&
        typeof document !== 'undefined' &&
        document.contains(openedFrom)
      ) {
        openedFrom.focus();
      }
    };
  }, [isOpen, openedFrom]);

  // ── Initial focus + the Tab cycle ─────────────────────────────────────────────────────────
  // Both need the NODE, so both live here, keyed on it.
  //
  // Initial focus is SYNCHRONOUS. The 50 ms timer this replaced raced real users: a click into a
  // field inside the dialog had its focus stolen back, redirecting the next keystrokes. React
  // flushes this effect before dispatching the next user event, so a synchronous grab can never
  // land mid-interaction. If focus is already inside the dialog (a child's mount effect runs
  // before this parent effect, or the user has since moved it), it is respected — which is also
  // what makes this effect safe to re-run when the node changes.
  //
  // 🔴 The FALLBACK is not a nicety, and resolving this row in page-shell's favour without it was
  // a hole in the first cut of this hook. WAI-ARIA APG: *"When a dialog opens, focus moves to an
  // element inside the dialog."* page-shell's `useModalLifecycle` moved focus ONLY when given an
  // `initialFocusRef`, so every `<Modal>` in the fleet that did not pass one left focus on the
  // trigger — outside the dialog. That is worse than it sounds now that this hook owns the Tab
  // cycle: the cycle is bound to the container, so with focus still outside, Tab is not
  // intercepted at all and the first Tab lands on whatever follows the trigger in the page.
  //
  // 🔴 And the cycle itself: the half page-shell never had. A dialog claiming `aria-modal="true"`
  // that a Tab can leave is not modal — for the emergency-broadcast `alertdialog`, which blocks
  // the whole app, it meant a keyboard user could tab into an application they were supposedly
  // locked out of.
  useEffect(() => {
    if (!isOpen || !container) return undefined;

    if (!container.contains(document.activeElement)) {
      const explicit =
        initialFocusRef &&
        initialFocusRef.current &&
        typeof initialFocusRef.current.focus === 'function'
          ? initialFocusRef.current
          : null;
      // `container` itself is the last resort — `<AlertDialog>` and `<Modal>` both give it
      // `tabIndex={-1}` precisely so a dialog with no focusable content is still reachable
      // rather than leaving the reader stranded outside it.
      const target = explicit || container.querySelector(FOCUSABLE_SELECTOR) || container;
      if (target && typeof target.focus === 'function') target.focus();
    }

    const handleTabKey = (event) => {
      if (event.key !== 'Tab') return;
      // Re-queried per keystroke, not captured at bind: a dialog whose content changes (a wizard
      // step, a revealed error) must cycle over what is on screen NOW. The pos-web copy captured
      // first/last at bind time and went stale.
      const focusable = container.querySelectorAll(FOCUSABLE_SELECTOR);
      // 0 focusables: nothing to cycle between. 1: the browser's own behaviour would leave the
      // dialog, but wrapping to the single element is what `aria-modal` promises, so fall through
      // and let the two arms below both resolve to it.
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handleTabKey);
    return () => container.removeEventListener('keydown', handleTabKey);
  }, [isOpen, container, initialFocusRef]);

  // ── Backdrop dismissal ────────────────────────────────────────────────────────────────────
  // PLANNER-PALM-UAT-2026-11-03-001 U4: a PAIR of press handlers and the click, not a click
  // handler alone. A press that began inside the dialog and was released over the backdrop fires
  // `click` ON the backdrop (the down and up targets' common ancestor), so `target ===
  // currentTarget` alone closed a dialog the person was still working in. See
  // `useBackdropDismissal`, which owns the rule.
  const backdropProps = useBackdropDismissal({ closeOnBackdrop, onClose });

  return { containerRef, titleId, backdropProps };
}

export default useModalChrome;
